import React from "react";
import { motion } from "framer-motion";
import {
  Phone,
  MessageCircle,
  Briefcase,
  BadgeCheck,
  Clock3,
  Sparkles,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";

const trustItems = [
  { icon: BadgeCheck, label: "Trusted Employers" },
  { icon: Clock3, label: "Easy Process" },
  { icon: Briefcase, label: "Multiple Job Roles" },
];

const miniRoles = ["Maid", "Driver", "Cook", "Nurse", "Caretaker", "Nanny"];

const floatingBadges = [
  { icon: ShieldCheck, label: "Trusted", top: "10%", left: "4%" },
  { icon: Briefcase, label: "Jobs", top: "18%", right: "7%" },
  { icon: CheckCircle2, label: "Simple", bottom: "28%", left: "6%" },
  { icon: Sparkles, label: "Quick", bottom: "16%", right: "8%" },
];

const JobsHero = () => {
  const formLink = "https://forms.gle/EiqVhxZbkbqaGYZm9";

  return (
    <section className="relative overflow-hidden min-h-[88vh] md:min-h-[95vh] flex items-center">
      {/* Base background */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#ffffff_0%,#eef8ff_42%,#fff6ef_100%)]" />

      {/* Mesh glow layers */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_18%,rgba(56,189,248,0.22),transparent_26%),radial-gradient(circle_at_85%_22%,rgba(99,102,241,0.14),transparent_22%),radial-gradient(circle_at_78%_78%,rgba(251,146,60,0.20),transparent_28%),radial-gradient(circle_at_22%_82%,rgba(14,165,233,0.10),transparent_24%)]" />

      {/* Soft blobs */}
      <div className="absolute -top-20 -left-16 w-56 h-56 rounded-full bg-sky-200/45 blur-3xl" />
      <div className="absolute top-20 right-[-60px] w-52 h-52 rounded-full bg-indigo-200/35 blur-3xl" />
      <div className="absolute bottom-[-80px] right-[-40px] w-64 h-64 rounded-full bg-orange-200/40 blur-3xl" />

      {/* Grid texture */}
      <div className="absolute inset-0 opacity-[0.05] [background-image:linear-gradient(to_right,#0f172a_1px,transparent_1px),linear-gradient(to_bottom,#0f172a_1px,transparent_1px)] [background-size:32px_32px]" />

      {/* Top curved art */}
      <svg
        className="absolute top-0 left-0 w-full h-[180px] md:h-[240px] opacity-50"
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
        fill="none"
      >
        <path
          d="M0,96L60,90.7C120,85,240,75,360,74.7C480,75,600,85,720,90.7C840,96,960,96,1080,80C1200,64,1320,32,1380,16L1440,0L1440,0L0,0Z"
          fill="rgba(56,189,248,0.10)"
        />
        <path
          d="M0,160L80,144C160,128,320,96,480,101.3C640,107,800,149,960,154.7C1120,160,1280,128,1360,112L1440,96L1440,0L0,0Z"
          fill="rgba(251,146,60,0.08)"
        />
      </svg>

      {/* Floating art badges - desktop */}
      <div className="absolute inset-0 hidden md:block pointer-events-none">
        {floatingBadges.map((badge, idx) => {
          const Icon = badge.icon;
          return (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 + idx * 0.08 }}
              className="absolute"
              style={{
                top: badge.top,
                left: badge.left,
                right: badge.right,
                bottom: badge.bottom,
              }}
            >
              <div className="flex items-center gap-2 rounded-full border border-white/80 bg-white/75 backdrop-blur-xl px-4 py-2 shadow-[0_10px_30px_rgba(15,23,42,0.08)]">
                <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-sky-50 to-orange-50 border border-slate-100">
                  <Icon className="w-4 h-4 text-indigo-600" />
                </div>

                <span className="text-sm font-medium text-gray-700">
                  {badge.label}
                </span>
              </div>
            </motion.div>
          );
        })}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 pt-28 pb-16 md:pt-36 md:pb-24 w-full">
        <motion.div
          initial={{ opacity: 0, y: 35 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="max-w-4xl mx-auto text-center"
        >
          {/* Badge */}
          <span className="inline-flex items-center rounded-full border border-sky-200 bg-white/85 backdrop-blur px-4 py-2 text-xs sm:text-sm text-sky-700 font-medium mb-5 shadow-sm">
            Trusted Job Opportunities
          </span>

          {/* Heading */}
          <h1 className="text-[2.3rem] sm:text-5xl md:text-6xl font-bold leading-[1.02] text-gray-900 mb-4">
            Looking for Work?
            <br />
            <span className="bg-gradient-to-r from-indigo-600 via-sky-600 to-orange-500 bg-clip-text text-transparent">
              Apply Easily with A2Z Consultancy
            </span>
          </h1>

          {/* Subtext */}
          <p className="text-sm sm:text-base md:text-xl text-gray-700 max-w-3xl mx-auto mb-8 leading-relaxed">
            We connect you with trusted families and businesses for jobs like
            maid, driver, cook, nanny, caretaker, nurse, and more.
          </p>

          {/* CTA buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 mb-8">
            <a
              href="https://wa.me/919665855163?text=Hi%20A2Z%20Consultancy,%20I%20am%20looking%20for%20work."
              target="_blank"
              rel="noreferrer"
              className="w-full sm:w-auto"
            >
              <button className="w-full sm:min-w-[235px] px-6 py-4 rounded-2xl bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white font-semibold text-base md:text-lg shadow-[0_14px_35px_rgba(18,140,126,0.28)] hover:scale-[1.02] transition flex items-center justify-center gap-2">
                <MessageCircle className="w-5 h-5" />
                Apply on WhatsApp
              </button>
            </a>

            <a
              href={formLink}
              target="_blank"
              rel="noreferrer"
              className="w-full sm:w-auto"
            >
              <button className="w-full sm:min-w-[190px] px-6 py-4 rounded-2xl bg-white/90 text-gray-900 border border-sky-100 font-semibold text-base md:text-lg shadow-md hover:scale-[1.02] transition flex items-center justify-center gap-2">
                <Phone className="w-5 h-5 text-sky-600" />
                Apply Now
              </button>
            </a>
          </div>

          {/* Trust chips */}
          <div className="flex flex-wrap justify-center gap-3 max-w-3xl mx-auto mb-8">
            {trustItems.map((item, index) => {
              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/80 backdrop-blur border border-white/80 text-sm md:text-base text-gray-700 shadow-sm"
                >
                  <Icon className="w-4 h-4 text-indigo-600" />
                  {item.label}
                </div>
              );
            })}
          </div>

          {/* Decorative roles art panel */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="relative max-w-4xl mx-auto"
          >
            <div className="relative overflow-hidden rounded-[30px] border border-white/70 bg-white/65 backdrop-blur-2xl shadow-[0_20px_50px_rgba(15,23,42,0.10)] px-4 py-5 sm:px-6 sm:py-6">
              {/* Panel inner art */}
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(56,189,248,0.10),transparent_22%),radial-gradient(circle_at_80%_80%,rgba(251,146,60,0.10),transparent_22%)]" />

              <div className="absolute inset-0 opacity-[0.06] [background-image:radial-gradient(#0f172a_0.8px,transparent_0.8px)] [background-size:16px_16px]" />

              <div className="relative">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                  <div className="text-left">
                    <p className="text-sm font-semibold text-indigo-700 mb-1">
                      Popular Work Categories
                    </p>

                    <p className="text-sm text-gray-600">
                      Choose the type of work that fits you best
                    </p>
                  </div>

                  <div className="hidden sm:flex items-center gap-2">
                    <span className="w-3 h-3 rounded-full bg-sky-400" />
                    <span className="w-3 h-3 rounded-full bg-indigo-400" />
                    <span className="w-3 h-3 rounded-full bg-orange-400" />
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 sm:gap-3 justify-start sm:justify-center">
                  {miniRoles.map((role, idx) => (
                    <div
                      key={role}
                      className={`px-4 py-2.5 rounded-full text-sm font-medium border shadow-sm ${
                        idx % 3 === 0
                          ? "bg-sky-50 text-sky-700 border-sky-100"
                          : idx % 3 === 1
                          ? "bg-indigo-50 text-indigo-700 border-indigo-100"
                          : "bg-orange-50 text-orange-700 border-orange-100"
                      }`}
                    >
                      {role}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Decorative lower glow */}
            <div className="absolute -z-10 left-1/2 -translate-x-1/2 bottom-[-28px] w-[82%] h-16 rounded-full bg-gradient-to-r from-sky-200/50 via-indigo-200/30 to-orange-200/50 blur-2xl" />
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom wave art */}
      <svg
        className="absolute bottom-0 left-0 w-full h-[90px] md:h-[120px]"
        viewBox="0 0 1440 320"
        preserveAspectRatio="none"
      >
        <path
          fill="rgba(255,255,255,0.7)"
          d="M0,224L60,213.3C120,203,240,181,360,181.3C480,181,600,203,720,192C840,181,960,139,1080,144C1200,149,1320,203,1380,229.3L1440,256L1440,320L0,320Z"
        />
      </svg>
    </section>
  );
};

export default JobsHero;